import React, { useState, useEffect } from "react";
import { Mail, MapPin, Send, CheckCircle2, Copy } from "lucide-react";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Contact Communications — Quivix";
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  const copyEmail = () => {
    navigator.clipboard.writeText("support@quivixdigital.com");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-black text-[#f5f5f7] min-h-screen pt-36 pb-24 font-sans selection:bg-blue-500/30">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        
        {/* Header Title */}
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-mono uppercase tracking-widest text-blue-400">Direct Inquiries</span>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-white mt-3 font-heading">
            Connect with QuivixDigital.
          </h1>
          <p className="mt-4 text-base text-[#86868b] leading-relaxed">
            Whether you are seeking product partnerships, software solutions, or technical support, our team is at your service.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Contact Options / Channels (5 Cols) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            
            {/* Quick Email Card */}
            <div className="apple-glass rounded-3xl p-8 border border-white/[0.08] relative overflow-hidden">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center mb-6">
                <Mail className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-mono text-[#86868b] uppercase tracking-wider">Primary Support Channel</span>
              <h3 className="text-xl font-bold text-white mt-1 font-heading">support@quivixdigital.com</h3>
              <p className="text-xs text-[#86868b] mt-2 leading-relaxed">
                Response SLA: Within 4 business hours for enterprise and investor requests.
              </p>

              <button
                onClick={copyEmail}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-xs text-white transition-all active:scale-95"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>{copied ? "Copied to Clipboard!" : "Copy Email Address"}</span>
              </button>
            </div>

            {/* Global Offices */}
            <div className="apple-glass rounded-3xl p-8 border border-white/[0.08] flex flex-col gap-5">
              <span className="text-[11px] font-mono text-[#86868b] uppercase tracking-wider">Dual Global Headquarters</span>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-blue-400 shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-white font-heading">Americas HQ</h4>
                    <span className="text-[9px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full">Main Office</span>
                  </div>
                  <p className="text-xs text-[#86868b] mt-0.5">New Mexico, United States</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pt-4 border-t border-white/[0.06]">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-emerald-400 shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-white font-heading">Middle East HQ</h4>
                    <span className="text-[9px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full">Main Office</span>
                  </div>
                  <p className="text-xs text-[#86868b] mt-0.5">Jeddah, Kingdom of Saudi Arabia</p>
                </div>
              </div>
            </div>

          </div>

          {/* Contact Form (7 Cols) */}
          <div className="lg:col-span-7 apple-glass rounded-3xl p-8 sm:p-12 border border-white/[0.08]">
            <h3 className="text-2xl font-bold text-white font-heading mb-2">Send an Inquiry</h3>
            <p className="text-xs text-[#86868b] mb-8">Fill out the encrypted form below to route your message to the appropriate department.</p>

            {submitted ? (
              <div className="py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h4 className="text-xl font-bold text-white font-heading">Message Dispatched</h4>
                <p className="text-xs text-[#86868b] mt-2 max-w-sm mx-auto leading-relaxed">
                  Thank you for reaching out to Quivix. Our team will review your inquiry and respond shortly.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-xs text-white transition-all"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[11px] font-mono text-[#86868b] block mb-1">Your Full Name</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Jane Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-mono text-[#86868b] block mb-1">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="jane@organization.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-mono text-[#86868b] block mb-1">Inquiry Subject</label>
                  <input
                    type="text"
                    name="subject"
                    placeholder="Partnerships / Investment / Support"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-mono text-[#86868b] block mb-1">Message Details</label>
                  <textarea
                    name="message"
                    rows="5"
                    placeholder="Provide details about your project or request..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full mt-2 py-3.5 rounded-full bg-white text-black font-semibold text-xs hover:bg-neutral-200 transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.2)] active:scale-95"
                >
                  <span>Dispatch Message</span>
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default ContactPage;
